/**
 * Pantalla del carro (CHK-01, WSP-02).
 *
 * No se indexa: es el carro de una persona en un navegador, no una pagina del
 * catalogo. Por lo mismo no lleva datos estructurados.
 *
 * Tiene dos salidas y las dos importan. "Ir a pagar" lleva al checkout, donde se
 * eligen entrega y documento y se verifica el stock (CHK-03, CHK-04, CHK-05).
 * WhatsApp queda debajo porque el pago con tarjeta todavia no existe —Webpay
 * quedo fuera de esta etapa— y porque hay quien prefiere cotizar sin llenar un
 * formulario: el mensaje va con el pedido completo ya escrito.
 *
 * Con despacho y retiro apagados desde el panel, la tienda no cobra: "Ir a
 * pagar" desaparece y WhatsApp queda como la unica salida, con un aviso que lo
 * explica (`utilidades/entrega.ts`).
 */
import { Link } from 'react-router-dom';

import { Icono } from '../componentes/comunes/Icono';
import { LineaCarro } from '../componentes/carro/LineaCarro';
import { useCarro } from '../contexto/carro-contexto';
import { useDespacho } from '../contexto/despacho-contexto';
import { Seo } from '../seo/Seo';
import { registrarClicWhatsapp } from '../servicios/metricas.servicio';
import { ofreceDespacho, ofreceRetiro, soloWhatsapp, umbralDespachoGratis } from '../utilidades/entrega';
import { pesos } from '../utilidades/formato';
import { rutas } from '../utilidades/rutas';
import { whatsappCarro } from '../utilidades/whatsapp';

export function Carro() {
  const { carro, unidades, cambiarCantidad, quitar } = useCarro();
  const { opciones } = useDespacho();
  const vacio = carro.items.length === 0;

  // `null` mientras no se sepa el umbral —no se promete un despacho gratis con
  // un monto que podria no ser el vigente— y tambien con el despacho o el
  // despacho gratis apagados desde el panel: no hay gratis que alcanzar.
  const conDespacho = ofreceDespacho(opciones);
  const umbralGratis = umbralDespachoGratis(opciones);
  const faltaParaDespachoGratis = umbralGratis !== null ? umbralGratis - carro.subtotal : null;
  const porWhatsapp = soloWhatsapp(opciones);

  // Que dice la fila de la entrega segun lo que se ofrece hoy.
  const valorEntrega = conDespacho
    ? faltaParaDespachoGratis !== null && faltaParaDespachoGratis <= 0
      ? 'Gratis'
      : 'Se calcula al finalizar'
    : ofreceRetiro(opciones)
      ? 'Retiro en tienda, gratis'
      : porWhatsapp
        ? 'Se acuerda por WhatsApp'
        : 'Se calcula al finalizar';

  return (
    <>
      <Seo
        titulo="Tu carro"
        descripcion="Los productos que agregaste al carro en El Carpintero."
        ruta={rutas.carro()}
        sinIndexar
      />

      <div className="contenedor pagina">
        {vacio ? (
          <section className="vacio">
            <p className="vacio-icono">
              <Icono nombre="carro" />
            </p>
            <h1>Tu carro está vacío</h1>
            <p>
              Todavía no agregaste nada. Explora el catálogo o escríbenos si buscas algo puntual y
              no lo encuentras.
            </p>
            <p>
              <Link className="btn btn-primario" to={rutas.inicio()}>
                Ver el catálogo
              </Link>
            </p>
          </section>
        ) : (
          <>
            <header className="encabezado-pag">
              <div>
                <h1>Tu carro</h1>
                <p className="sub">
                  {unidades} {unidades === 1 ? 'unidad' : 'unidades'} ·{' '}
                  {carro.items.length} {carro.items.length === 1 ? 'producto' : 'productos'}
                </p>
              </div>
            </header>

            <div className="carro">
              <section className="carro-lista" aria-labelledby="lineas-carro">
                <h2 className="solo-lectores" id="lineas-carro">
                  Productos en el carro
                </h2>
                <ul>
                  {carro.items.map((item) => (
                    <LineaCarro
                      key={item.producto.sku}
                      item={item}
                      alCambiarCantidad={(cantidad) => cambiarCantidad(item.producto.sku, cantidad)}
                      alQuitar={() => quitar(item.producto.sku)}
                    />
                  ))}
                </ul>
              </section>

              <aside className="carro-resumen" aria-labelledby="resumen-carro">
                <h2 id="resumen-carro">Resumen</h2>

                <p className="carro-fila">
                  <span>Subtotal</span>
                  <b>{pesos(carro.subtotal)}</b>
                </p>
                <p className="carro-fila carro-fila-tenue">
                  <span>{conDespacho ? 'Despacho' : 'Entrega'}</span>
                  <span>{valorEntrega}</span>
                </p>

                {faltaParaDespachoGratis !== null && faltaParaDespachoGratis > 0 ? (
                  <p className="carro-aviso">
                    <Icono nombre="camion" />
                    Te faltan {pesos(faltaParaDespachoGratis)} para el despacho gratis.
                  </p>
                ) : null}

                <p className="carro-iva">Precios con IVA incluido.</p>

                {/*
                  Ir a pagar lleva al checkout, donde se eligen entrega y
                  documento y se verifica el stock (CHK-03, CHK-04, CHK-05). El
                  pago con tarjeta todavia no existe —la pasarela quedo fuera de
                  esta etapa— y por eso WhatsApp sigue aca abajo, como salida
                  directa para quien prefiere cotizar sin llenar un formulario.
                */}
                {porWhatsapp ? (
                  <div className="entrega-whatsapp">
                    <p className="t">Por ahora vendemos por WhatsApp</p>
                    <p>
                      Envíanos tu pedido y te confirmamos el stock, la entrega y la forma de pago.
                    </p>
                  </div>
                ) : (
                  <Link className="btn btn-lg btn-primario btn-bloque" to={rutas.checkout()}>
                    <Icono nombre="tarjeta" />
                    Ir a pagar
                  </Link>
                )}

                <a
                  className="btn btn-lg btn-wa btn-bloque"
                  href={whatsappCarro(carro)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => registrarClicWhatsapp({ origen: 'carro' })}
                >
                  <Icono nombre="wa" />
                  {porWhatsapp ? 'Enviar pedido por WhatsApp' : 'Cotizar por WhatsApp'}
                </a>

                <p className="carro-seguir">
                  <Link to={rutas.inicio()}>Seguir comprando</Link>
                </p>
              </aside>
            </div>
          </>
        )}
      </div>
    </>
  );
}
